/**
 * PAWMATE ANIMATION SYSTEM - JavaScript Controller
 * TrueBond-Inspired Premium Micro-Interactions
 *
 * This script handles:
 * 1. Scroll-triggered reveal animations
 * 2. Staggered grid animations
 * 3. Dynamic animation triggers
 * 4. Performance optimization
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  const CONFIG = {
    // Intersection Observer settings
    observer: {
      rootMargin: '0px 0px -60px 0px', // Trigger slightly before element enters viewport
      threshold: 0.15                   // 15% of element must be visible
    },

    // CSS classes
    classes: {
      reveal: 'reveal-on-scroll',
      revealed: 'revealed',
      staggerParent: 'data-reveal-stagger',
      willAnimate: 'will-animate',
      animationDone: 'animation-done'
    },

    // Performance
    performance: {
      debounceDelay: 16,              // ~60fps
      cleanupDelay: 1000              // Remove will-change after animation
    }
  };


  // ============================================
  // SCROLL REVEAL SYSTEM
  // ============================================

  /**
   * Initialize scroll reveal animations
   * Uses Intersection Observer for performance
   */
  function initScrollReveal() {
    const revealElements = document.querySelectorAll(`.${CONFIG.classes.reveal}`);

    if (!revealElements.length) return;

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Show all elements immediately for reduced motion users
      revealElements.forEach(el => {
        el.classList.add(CONFIG.classes.revealed);
      });
      return;
    }

    // Create Intersection Observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;

          // Add will-change for performance boost
          element.classList.add(CONFIG.classes.willAnimate);

          // Trigger reveal animation
          element.classList.add(CONFIG.classes.revealed);

          // Stop observing this element
          observer.unobserve(element);

          // Clean up will-change after animation completes
          setTimeout(() => {
            element.classList.remove(CONFIG.classes.willAnimate);
            element.classList.add(CONFIG.classes.animationDone);
          }, CONFIG.performance.cleanupDelay);
        }
      });
    }, CONFIG.observer);

    // Observe all reveal elements
    revealElements.forEach(el => observer.observe(el));
  }


  // ============================================
  // STAGGERED GRID ANIMATIONS
  // ============================================

  /**
   * Initialize staggered animations for grid children
   * Adds delay to each child for cascading effect
   */
  function initStaggeredGrids() {
    const staggeredParents = document.querySelectorAll('[data-reveal-stagger]');

    if (!staggeredParents.length) return;

    staggeredParents.forEach(parent => {
      const children = parent.querySelectorAll(`.${CONFIG.classes.reveal}`);
      const staggerDelay = parseInt(parent.dataset.revealStagger) || 80; // ms

      children.forEach((child, index) => {
        child.style.transitionDelay = `${index * staggerDelay}ms`;
      });
    });
  }


  // ============================================
  // BUTTON ANIMATIONS
  // ============================================

  /**
   * Add tactile feedback to buttons
   * Subtle press effect on mousedown/touchstart
   */
  function initButtonAnimations() {
    const buttons = document.querySelectorAll('.btn, .btn-animate, button[type="submit"]');

    if (!buttons.length) return;

    buttons.forEach(button => {
      // Skip if already has custom animation class
      if (button.classList.contains('no-animate')) return;

      // Add animation class if not present
      if (!button.classList.contains('btn-animate')) {
        button.classList.add('btn-animate');
      }
    });
  }


  // ============================================
  // IMAGE ZOOM INITIALIZATION
  // ============================================

  /**
   * Auto-apply zoom animation to images in cards
   */
  function initImageZoom() {
    // Find all card-hover elements with images
    const cardsWithImages = document.querySelectorAll('.card-hover img, .review-card img, .feature-card img, .benefit-card img');

    cardsWithImages.forEach(img => {
      // Skip if already has zoom class
      if (img.classList.contains('image-zoom') || img.classList.contains('no-zoom')) return;

      // Find parent container and add overflow hidden if needed
      const container = img.parentElement;
      if (container && !container.classList.contains('image-zoom-container')) {
        const computedStyle = window.getComputedStyle(container);
        if (computedStyle.overflow !== 'hidden') {
          container.style.overflow = 'hidden';
        }
      }

      // Add zoom class to image
      img.classList.add('image-zoom');
    });
  }


  // ============================================
  // ACCORDION ANIMATIONS
  // ============================================

  /**
   * Initialize smooth accordion animations
   */
  function initAccordions() {
    const accordionTriggers = document.querySelectorAll('[data-accordion-trigger]');

    accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', function() {
        const targetId = this.dataset.accordionTarget || this.getAttribute('aria-controls');
        const target = document.getElementById(targetId) || this.nextElementSibling;
        const parent = this.closest('[data-accordion-item]') || this.parentElement;

        if (!target) return;

        const isExpanded = parent.classList.contains('expanded');

        if (isExpanded) {
          parent.classList.remove('expanded');
          this.setAttribute('aria-expanded', 'false');
        } else {
          parent.classList.add('expanded');
          this.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }


  // ============================================
  // COUNTER ANIMATIONS
  // ============================================

  /**
   * Animate number counting
   * @param {HTMLElement} element - Element containing the target number
   * @param {number} duration - Animation duration in ms
   */
  function animateCounter(element, duration = 1500) {
    const targetText = element.textContent;
    const targetNumber = parseFloat(targetText.replace(/[^\d.]/g, ''));
    const suffix = targetText.replace(/[\d.]/g, '').trim();
    const hasDecimal = targetText.includes('.');
    const decimalPlaces = hasDecimal ? (targetText.split('.')[1] || '').replace(/[^\d]/g, '').length : 0;

    if (isNaN(targetNumber)) return;

    const startTime = performance.now();
    const startValue = 0;

    function updateCounter(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (targetNumber - startValue) * easeProgress;

      if (hasDecimal) {
        element.textContent = currentValue.toFixed(decimalPlaces) + suffix;
      } else {
        element.textContent = Math.round(currentValue) + suffix;
      }

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    }

    requestAnimationFrame(updateCounter);
  }

  /**
   * Initialize counter animations on scroll
   */
  function initCounterAnimations() {
    const counters = document.querySelectorAll('[data-animate-counter]');

    if (!counters.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const duration = parseInt(entry.target.dataset.counterDuration) || 1500;
          animateCounter(entry.target, duration);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    counters.forEach(counter => observer.observe(counter));
  }


  // ============================================
  // HOVER INTENT DETECTION
  // ============================================

  /**
   * Add hover intent detection for smoother interactions
   * Prevents rapid hover flashing on fast mouse movements
   */
  function initHoverIntent() {
    const hoverElements = document.querySelectorAll('[data-hover-intent]');

    hoverElements.forEach(element => {
      let hoverTimeout;
      const delay = parseInt(element.dataset.hoverDelay) || 100;

      element.addEventListener('mouseenter', () => {
        hoverTimeout = setTimeout(() => {
          element.classList.add('hover-active');
        }, delay);
      });

      element.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimeout);
        element.classList.remove('hover-active');
      });
    });
  }


  // ============================================
  // SMOOTH SCROLL LINKS
  // ============================================

  /**
   * Initialize smooth scroll for anchor links
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');

        if (targetId === '#' || targetId === '') return;

        const target = document.querySelector(targetId);
        if (!target) return;

        e.preventDefault();

        const headerOffset = 80; // Account for fixed header
        const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      });
    });
  }


  // ============================================
  // PERFORMANCE UTILITIES
  // ============================================

  /**
   * Debounce function for performance
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Check if element is in viewport
   */
  function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }


  // ============================================
  // LAZY ANIMATION LOADING
  // ============================================

  /**
   * Only initialize animations for elements that exist on the page
   * Improves initial load performance
   */
  function initAnimations() {
    // Core animations (always run)
    initScrollReveal();
    initStaggeredGrids();

    // Optional animations (check if elements exist)
    if (document.querySelector('.btn, .btn-animate')) {
      initButtonAnimations();
    }

    if (document.querySelector('.card-hover, .review-card, .feature-card, .benefit-card')) {
      initImageZoom();
    }

    if (document.querySelector('[data-accordion-trigger]')) {
      initAccordions();
    }

    if (document.querySelector('[data-animate-counter]')) {
      initCounterAnimations();
    }

    if (document.querySelector('[data-hover-intent]')) {
      initHoverIntent();
    }

    // Smooth scroll (always useful)
    initSmoothScroll();
  }


  // ============================================
  // INITIALIZE ON DOM READY
  // ============================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAnimations);
  } else {
    initAnimations();
  }


  // ============================================
  // RE-INITIALIZE ON SHOPIFY SECTION RENDER
  // ============================================
  // For Shopify theme editor compatibility

  document.addEventListener('shopify:section:load', function(event) {
    // Re-initialize animations for newly loaded sections
    const section = event.target;

    // Find reveal elements in the new section
    const newRevealElements = section.querySelectorAll(`.${CONFIG.classes.reveal}`);
    if (newRevealElements.length) {
      newRevealElements.forEach(el => {
        el.classList.remove(CONFIG.classes.revealed);
      });
      initScrollReveal();
    }

    // Re-initialize other animations
    initStaggeredGrids();
    initImageZoom();
  });


  // ============================================
  // EXPORT FOR EXTERNAL USE
  // ============================================

  window.PawmateAnimations = {
    initScrollReveal,
    initStaggeredGrids,
    initButtonAnimations,
    initImageZoom,
    animateCounter,
    isInViewport,
    reinit: initAnimations
  };

})();
